import React, { Component } from 'react';
import PropTypes from 'prop-types';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { defineMessages, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import classnames from 'classnames';
import { ShareIcon } from '@/shared/components/Icons';
import DataCacheService from '@/shared/services/DataCacheService';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import MultiSelect from './input/multiselect/MultiSelect';
import { getUserClassName } from './input/UserSearch';

const messages = defineMessages({
  Users: 'Users',
  ShareItem: 'Share {item}',
  Remark: 'Remark',
  ItemWasShared: 'Item was shared',
  Share: 'Share',
});

class ShareItemModal extends Component {
  state = {
    users: [],
    selectedUsers: [],
    remark: '',
  };

  componentDidMount() {
    this.form = React.createRef();
    const users = DataCacheService.getUsers();

    this.setState({
      users: users.map(x => ({
        id: x.id,
        email: x.email,
        label: x.name,
        languageCode: x.languageCode,
        isActive: x.isActive,
      })),
    });
  }

  static propTypes = {
    intl: intlShape.isRequired,
    itemName: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    relationName: PropTypes.string,
    relationId: PropTypes.string,
    id: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
  };

  static defaultProps = {
    show: false,
    relationName: '',
    relationId: undefined,
  };

  assignedToChanged = assignedTo => {
    this.setState(state => ({
      ...state,
      selectedAssignedTo: assignedTo,
    }));
  };

  userRemoved = item => {
    const { selectedUsers } = this.state;
    const index = selectedUsers.findIndex(x => x === item.value.id);

    const copy = [...selectedUsers];
    copy.splice(index, 1);

    this.setState({ selectedUsers: copy });
  };

  userAdded = selectedItem => {
    if (!selectedItem) return;

    const { selectedUsers } = this.state;

    if (!selectedUsers) {
      this.setState({
        selectedUsers: [selectedItem],
        invalidUsers: false,
      });
    } else {
      if (selectedUsers.map(x => x.id).includes(selectedItem.id)) return;
      this.setState({
        selectedUsers: [...selectedUsers, selectedItem],
        invalidUsers: false,
      });
    }
  };

  createTaskHistoryLine = model => {
    if ((model.notificationType !== 1000 && model.notificationType !== 1003) || !model.objectId) {
      const { intl } = this.props;
      ToastrService.success(intl.formatMessage(messages.ItemWasShared));
      this.closeModal();
      return;
    }

    const taskModel = {
      taskId: model.objectId,
      taskLevel: model.notificationType === 1003 ? 2 : null,
      sharedWith: model.recipients.map(x => x.userId),
      remark: model.shareMessage,
    };
    return api.base
      .post(`${config.appUrls.tasks}/api/PlannedTasks/TaskShared`, taskModel)
      .then(() => {
        const { intl } = this.props;
        ToastrService.success(intl.formatMessage(messages.ItemWasShared));
        this.closeModal();
      });
  };

  onSave = () => {
    const { selectedUsers, remark } = this.state;
    const { type, itemName, id, relationName, relationId } = this.props;
    if (!selectedUsers || selectedUsers.length < 1) {
      this.setState({ invalidUsers: true });
      return;
    }

    let notificationType;
    switch (type) {
      case 'task':
        notificationType = 1000;
        break;
      case 'document':
        notificationType = 1001;
        break;
      case 'relation':
        notificationType = 1002;
        break;
      case 'todo':
        notificationType = 1003;
        break;
      case 'interaction':
        notificationType = 1004;
        break;
      default:
    }

    const model = {
      shareMessage: remark,
      shareSubject:
        relationName && itemName
          ? `${relationName} - ${itemName}`
          : relationName && !itemName
          ? `${relationName}`
          : itemName,
      level: 0,
      notificationType,
      objectId: id,
      recipients: selectedUsers.map(x => ({
        userId: x.id,
        email: x.email,
        languageCode: x.languageCode,
      })),
      relationId,
    };

    return api.base
      .post(`${config.appUrls.notifications}/api/notifications/createshare`, model)
      .then(
        () => this.createTaskHistoryLine(model),
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  render() {
    const { show, intl, itemName } = this.props;
    const { users, selectedUsers, remark, invalidUsers } = this.state;
    const selectedItemsFormatted = users
      ? users.filter(r => selectedUsers.map(x => x.id).includes(r.id))
      : [];
    return (
      <OkCancelModal
        title={
          <span>
            <span className="margin5T">
              <ShareIcon className="vertical-align-middle margin5R" />
            </span>
            {intl.formatMessage(messages.ShareItem, { item: itemName })}
          </span>
        }
        show={show}
        okLabel={intl.formatMessage(messages.Share)}
        onClose={this.closeModal}
        onSave={this.onSave}
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal">
              <div className={classnames('form-group', { fieldError: invalidUsers })}>
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Users)}
                </label>
                <div className="col-md-10">
                  <MultiSelect
                    selectedItem={selectedItemsFormatted}
                    onItemRemoved={this.userRemoved}
                    onItemSelected={this.userAdded}
                    items={users}
                    itemClassNameFunc={getUserClassName}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-2">
                  {intl.formatMessage(messages.Remark)}
                </label>
                <div className="col-md-10">
                  <textarea
                    id="ShareItemRemarkField"
                    style={{ minHeight: '100px', fontSize: '14px' }}
                    rows="10"
                    maxLength={2000}
                    className="form-control input-sm vertical-resize"
                    value={remark || ''}
                    required
                    onChange={e =>
                      this.setState({
                        remark: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </OkCancelModal>
    );
  }
}

export default injectIntl(ShareItemModal);
